import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, Col, Flex, Form, Input, message, Modal, Row, Tabs, Tooltip } from "antd";
import Title from "antd/es/typography/Title";
import WarehouseService from "../../services/Warehouses/WarehouseService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import WarehouseDocksPanel from "./WarehouseDocksPanel";
import WarehouseReceivingZonesPanel from "./WarehouseReceivingZonesPanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { WAREHOUSE_SETTINGS_ROOT } from "../RouteConstants";
import WarehouseDelete from "./WarehouseDelete";
import { CountryCodeSelect, PortCodeSelect } from "../../helpers/common";
import AppButton from "../../common/AppButton";
import CustomButton from "../../common/CustomButton";
import { useGoBack } from "../../customHooks/useGoBack";

const OrgReferenceCodeTooltip = () => (
  <Tooltip title="This is a specific code reference to your external tool like an ERP system">
    <span>Org Reference Code <FontAwesomeIcon icon={faQuestionCircle} /></span>
  </Tooltip>
);

const WarehouseTabs = [
  {
    key: 1,
    label: "Receiving Zones",
    children: <WarehouseReceivingZonesPanel />,
  },
  {
    key: 2,
    label: "Docks",
    children: <WarehouseDocksPanel />,
  },
];

const WarehouseDetailsPage = () => {
  const { state: { warehouseId = "" } } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleCancel = () => navigate(WAREHOUSE_SETTINGS_ROOT);

  const cachedWarehouse = queryClient.getQueryData(["warehouses"])
    ?.find(warehouse => warehouse.id === +warehouseId);
  if (cachedWarehouse) {
    queryClient.setQueryData(["warehouses", warehouseId], cachedWarehouse);
  }
  const { data: warehouse } = useQuery({
    queryKey: ["warehouses", warehouseId],
    queryFn: async () => await WarehouseService.getWarehouseById(warehouseId),
    enabled: !cachedWarehouse && !!warehouseId,
  });

  const { mutate: createWarehouse } = useMutation({
    mutationFn: async (data) => await WarehouseService.createWarehouse(data),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["warehouses"] });
      navigate(`${WAREHOUSE_SETTINGS_ROOT}/details`, { state: { warehouseId: result.id } });
      message.success("Warehouse details saved successfully!");
      setShowSuccessModal(true);
    },
    onError: (error) =>
      message.error(`Failed to save warehouse details. ${error.response?.data?.message}, Please try again.`),
  });

  const { mutate: updateWarehouse } = useMutation({
    mutationFn: async (data) =>
      await WarehouseService.updateWarehouse(warehouseId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["warehouses"] });
      await queryClient.invalidateQueries({ queryKey: ["warehouses", warehouseId] });
      message.success("Warehouse details updated successfully!");
      if (receivingZones?.length !== 0) {
        handleCancel();
      }
    },
    onError: (error) =>
      message.error(`Failed to update warehouse details. ${error.response?.data?.message}, Please try again.`),
  });

  const { data: receivingZones } = useQuery({
    queryKey: [warehouseId, "receivingZones"],
    queryFn: async () => await WarehouseService.getReceivingZonesByWarehouseId(warehouseId),
  });

  useEffect(() => {
    form.setFieldsValue({
      ...warehouse,
    });
  }, [warehouse, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (warehouseId) {
        updateWarehouse(values);
      } else {
        createWarehouse(values);
      }
    } catch {
      // Do nothing, let the form handle the error messages
    }
  };

  const title = !warehouseId ? "New Warehouse" : "Warehouse Details";

  return (
    <>
      <div className="px-8 mb-4">
        <div className="flex justify-start items-center w-full font-inter">
          <Title level={3} className={"header-title"}>{title}</Title>
          <CustomButton
            type="link"
            onClick={useGoBack()}
            className="text-md font-medium !text-[#01A4A6] pr-4 pt-4"
          >
            / Warehouse
          </CustomButton>
        </div>
        <Form form={form} layout="vertical">
          <Card className="card-form">
            <Row gutter={[24, 16]} className="py-2">

              <Col xs={24} md={4}>
                <Form.Item name="name" label="Warehouse Name" rules={[{ required: true, message: "Please enter warehouse name" }]}>
                  <Input size="large" />
                </Form.Item>
                <Form.Item name="podCode" label="Port Of Discharge" rules={[{ required: true, message: "Please select port of discharge" }]}>
                  <PortCodeSelect size="large" />
                </Form.Item>
              </Col>

              <Col xs={24} md={4}>
                <Form.Item name="shortCode" label="Short Code" normalize={(v) => v?.trim()} rules={[{ required: true, message: "Please enter short code" }, { max: 10 }]}>
                  <Input size="large" />
                </Form.Item>
                <Form.Item name="orgReferenceCode" label={<OrgReferenceCodeTooltip />} normalize={(v) => v?.trim()}>
                  <Input size="large" />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item name={["address", "addressLine1"]} label="Address Line 1" rules={[{ required: true }]}>
                  <Input size="large" />
                </Form.Item>
                <Form.Item name={["address", "addressLine2"]} label="Address Line 2">
                  <Input size="large" />
                </Form.Item>
                <Row gutter={8}>
                  <Col span={10}>
                    <Form.Item name={["address", "zipCode"]} label="ZIP / Post" rules={[{ required: true }]}>
                      <Input size="large" />
                    </Form.Item>
                  </Col>
                  <Col span={14}>
                    <Form.Item name={["address", "city"]} label="City" rules={[{ required: true }]}>
                      <Input size="large" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name={["address", "state"]} label="State / District" rules={[{ required: true }]}>
                  <Input size="large" />
                </Form.Item>
                <Form.Item name={["address", "country"]} label="Country" rules={[{ required: true }]}>
                  <CountryCodeSelect size="large" />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item name="notes" label="Notes">
                  <Input.TextArea size="large" rows={11} placeholder="Enter any additional warehouse instructions..." />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Flex justify="end" align="center" gap={8} className="mt-4">
                  <AppButton variant="save" onClick={handleSubmit}>
                    Save
                  </AppButton>
                  {warehouseId &&
                    <WarehouseDelete warehouseId={warehouseId} queryClient={queryClient} />
                  }
                  <AppButton variant="close" onClick={handleCancel}>
                    Close
                  </AppButton>
                </Flex>
              </Col>
            </Row>
          </Card>
        </Form>
      </div >
      {warehouseId && (
        <div className="px-8 mb-4">
          <Card className="card-form" style={{ minHeight: 520 }}>
            <Tabs type="card"
              defaultActiveKey="1"
              items={WarehouseTabs} />
          </Card>
        </div>
      )
      }
      <Modal
        title="Next Step Required"
        centered
        open={showSuccessModal}
        onOk={() => setShowSuccessModal(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setShowSuccessModal(false)}>
            OK
          </Button>
        ]}
      >
        Warehouse details saved successfully. Please now enter Receiving Zones / Docks for this warehouse.
      </Modal>
    </>
  );
};

export default WarehouseDetailsPage;